// Libraries
import { Box, Flex, Text } from '@chakra-ui/react';
// Components
import { Checkbox } from '@atoms/index';
import { ColumnFilterProps, FilterVariant } from '@organisms/index';
// Utils
import text from '@translations/en.json';
// Types
import { Rating } from '@constants/index';
import { RatingsButtonsFilterProps } from './RatingsButtonsFilter.types';

/**
 * RatingsButtonsFilter component.
 *
 * @component
 * @template TableData - The type of data in the table.
 * @param {ColumnFilterProps<TableData, RatingsButtonsFilterProps>} props - The component props.
 * @param {Column<TableData, unknown>} props.column - The column filter props.
 * @param {RatingsButtonsFilterProps} props.props - The additional props specific to the ratings buttons filter.
 * @returns {ReactElement} - The JSX element representing the RatingsButtonsFilter component.
 */
export const RatingsButtonsFilter = <TableData,>({
  column,
  props,
  variant,
}: ColumnFilterProps<TableData, RatingsButtonsFilterProps>) => {
  const { getFilterValue, setFilterValue } = column;
  const { name } = props;
  const initialValue = (getFilterValue() as Rating[]) || [];
  const spacing = variant === FilterVariant.VISIBLE ? 2 : 4;

  const handleOnClick = (option: Rating) => {
    if (!initialValue.includes(option)) {
      setFilterValue([...initialValue, option]);
    } else {
      const valueFiltered = initialValue.filter((rating) => rating !== option);
      setFilterValue(valueFiltered);
    }
  };

  return (
    <Flex borderBottom='greyLight' direction='column'>
      <Flex
        bg='transparentScheme.700'
        borderTop='greyCheck'
        borderBottom='greyCheck'
        h={10}
        alignItems='center'
        justifyContent='center'
      >
        <Text textAlign='center' lineHeight={4} fontSize='xs' fontWeight='medium' p='0px 20px'>
          {name}
        </Text>
      </Flex>
      <Flex flexDirection='column' p={spacing} gap={spacing}>
        {Object.values(Rating).map((option) => (
          <Flex align='center' justify='center' key={option}>
            <Checkbox
              isSelected={initialValue.includes(option)}
              label={`${option} ${text.rating}`}
              onClick={() => handleOnClick(option)}
            />
            <Text ml={2} fontSize='m'>
              {option}
            </Text>
            <Box ml={1} w={3.5} h={3.5} bg={`ratings.${option}.dark`} borderRadius='base' />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
